/**
 * RangeIndicatorRenderer — Translucent ring overlay used to preview a
 * card's area-of-effect when the player hovers a card on the bar.
 *
 * Implemented as a DOM overlay (lightest path, no Three.js dependency)
 * so it stays unit-testable in the Node env where the rest of the UI
 * lives. The ring is positioned at viewport center by default — a
 * reasonable proxy for the swarm centroid from the player's POV. An
 * optional `project` callback in the constructor opts into per-call
 * 3D→2D projection if the caller wants pixel-accurate placement.
 */

import type { Vec3 } from '../../shared/utils';

export interface RangeIndicatorOpts {
  /** Optional ring color override (CSS color string). */
  color?: string;
  /** Optional opacity (0..1). Defaults to 0.35. */
  opacity?: number;
}

export interface RangeIndicatorProjector {
  /**
   * Project a world-space point to screen pixel coordinates. Return
   * `null` when the point is off-screen / behind the camera.
   */
  (centroid: Vec3): { x: number; y: number } | null;
}

/**
 * World-units-per-screen-pixel multiplier used when scaling the radius
 * into a viewport-sized ring. Tuned to feel reasonable without a real
 * camera projection: at radius=18 the ring is ~144px wide.
 */
export const PIXELS_PER_WORLD_UNIT = 4;

export class RangeIndicatorRenderer {
  private parent: HTMLElement;
  private ring: HTMLElement;
  private project: RangeIndicatorProjector | null;
  private visible = false;

  constructor(parentElement: HTMLElement, project?: RangeIndicatorProjector) {
    this.parent = parentElement;
    this.project = project ?? null;

    this.ring = document.createElement('div');
    this.ring.className = 'range-indicator hidden';
    this.parent.appendChild(this.ring);
  }

  /**
   * Show the ring at `centroid` with the given world-unit radius. When
   * a projector is configured we anchor at the projected screen pos;
   * otherwise we anchor at viewport-center (CSS handles centering).
   */
  showRangeAt(centroid: Vec3, radius: number, opts: RangeIndicatorOpts = {}): void {
    const safeRadius = Math.max(0, Number.isFinite(radius) ? radius : 0);
    const diameterPx = safeRadius * 2 * PIXELS_PER_WORLD_UNIT;

    this.ring.style.width = `${diameterPx}px`;
    this.ring.style.height = `${diameterPx}px`;

    if (this.project) {
      const screen = this.project(centroid);
      if (!screen) {
        this.hide();
        return;
      }
      this.ring.style.left = `${screen.x}px`;
      this.ring.style.top = `${screen.y}px`;
    } else {
      // Default: anchor center-of-screen via CSS.
      this.ring.style.left = '50%';
      this.ring.style.top = '50%';
    }
    this.ring.style.transform = 'translate(-50%, -50%)';

    if (opts.color) this.ring.style.borderColor = opts.color;
    if (opts.opacity !== undefined) this.ring.style.opacity = String(opts.opacity);

    this.ring.classList.remove('hidden');
    this.visible = true;
  }

  hide(): void {
    this.ring.classList.add('hidden');
    // Clear stale coords + per-call overrides so the next show doesn't
    // flash the previous frame's position or carry forward the prior
    // call's color/opacity (e.g. a Healer card hover painting the ring
    // red would otherwise persist red on the next Striker hover).
    // Width/height/transform are re-set by show.
    this.ring.style.left = '';
    this.ring.style.top = '';
    this.ring.style.borderColor = '';
    this.ring.style.opacity = '';
    this.visible = false;
  }

  isVisible(): boolean {
    return this.visible;
  }

  dispose(): void {
    if (this.ring.parentElement) {
      this.ring.parentElement.removeChild(this.ring);
    }
  }
}
